/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateTrendingNowV2Config } from 'state/thunks/trendingnowv2';
import { setTempTrendingNowConfig } from 'state/slices/trendingnowv2'
// material
import ConfigV2Controller from 'material/expandablesection/v2/configv2controller/configv2controller'
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg'
// content
import ArticleRedirect from './articleredirect/articleredirect';
import ImageFallback from './imagefallback/imagefallback';
// styles
import injectSheet from 'react-jss'
import styles from './advancedsection_styles'
import FeaturedArticle from './featuredarticle/featuredarticle';


/* ========== ~~~~~~~~~~ TRENDING NOW CONFIG V2: ADVANCED SECTION ~~~~~~~~~~ ========== */
const AdvancedSection = injectSheet(styles)((props) => {
  
  const dispatch = useDispatch()
  const { advancedConfig: savedAdvancedConfig, trendingNowId } = useSelector(state => state.trendingNowV2.trendingNowConfig)
  const { advancedConfig: tempAdvancedConfig } = useSelector(state => state.trendingNowV2.tempTrendingNowConfig)
  const { isConfigValid } = useSelector(state => state.trendingNowV2)

  const [isUpdating, setIsUpdating] = useState(false)
  const [isOpened, setIsOpened] = useState(false)

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async (e) => {
    e.stopPropagation()
    setIsUpdating(true)
    const advancedConfigRequest = {advancedConfig: {...tempAdvancedConfig}, section: 'ADVANCED'}
    const response = await dispatch(updateTrendingNowV2Config({ trendingNowConfig: {...advancedConfigRequest}, trendingNowId }))
    if(!response.error && !response.payload.error) setIsOpened(false)
    setIsUpdating(false)
  }, [trendingNowId, dispatch, tempAdvancedConfig])

  const handleCancel = useCallback(async() => {
    setIsOpened(false)
    dispatch(setTempTrendingNowConfig({
      configName: 'advancedConfig',
      data: savedAdvancedConfig
    }))
  }, [dispatch, savedAdvancedConfig])

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(tempAdvancedConfig) === JSON.stringify(savedAdvancedConfig));
    return isFieldsChanged
  }, [tempAdvancedConfig, savedAdvancedConfig])


  const handleAdvancedConfigChange = useCallback((newConfigValues) => {
    dispatch(setTempTrendingNowConfig({
      configName: 'advancedConfig',
      data: {
        ...tempAdvancedConfig,
        ...newConfigValues
      }
    }))
}, [dispatch, tempAdvancedConfig]);

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged || isConfigValid.advancedConfig === false}
      saveTitle={'Save Changes'}
      icon={<SettingsIcon />}
      title='Advanced'
    />
  );


  const advancedConfigData = useMemo((section) => {
    return {
      articleRedirect: {
        articleRedirect: tempAdvancedConfig.articleRedirect
      },
      fallbackImage: {
        fallbackImageType: tempAdvancedConfig.fallbackImageType,
        fallbackImageUrl: tempAdvancedConfig.fallbackImageUrl
      },
      featuredArticleDetails: tempAdvancedConfig.featuredArticleDetails
    }
  }, [tempAdvancedConfig])

  return (
    <ExpandableSection
      className={`expandablesectionv2 advancedsection ${props.classes.advancedsection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={savedAdvancedConfig.isUpdating}
      disableSave={!isChanged}
    >
      <FeaturedArticle {...advancedConfigData.featuredArticleDetails} isSectionOpen={isOpened} onChange={handleAdvancedConfigChange} />
      <ArticleRedirect {...advancedConfigData.articleRedirect} onChange={handleAdvancedConfigChange} />
      <ImageFallback {...advancedConfigData.fallbackImage} isSectionOpen={isOpened} onChange={handleAdvancedConfigChange} />
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default AdvancedSection;